import React, { useEffect, useRef, useState, useCallback } from 'react'

import Image from 'next/image'
import { useTranslation } from 'next-i18next'

import { Content } from 'components/elements/styled'
import NextImage from 'components/Image'
import SnapCarousel from 'components/SnapCarousel'
import { useIsMobile } from 'hooks/useIsMobile'

import { GooglePlayStoreLink, AppStoreLink } from '@enuygun/frontend-components'
import { EnFinans } from '@enuygun/icons'
import QRCode from 'react-qr-code'

import { getFinanceCardData } from './data'
import * as S from './Finance.styled'
import { useAppState } from 'context/AppContext'
import { useDeviceType } from 'hooks/useDeviceType'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'

const storeButtonResetStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  display: 'block',
  font: 'inherit',
  color: 'inherit',
}

const LazyFinanceSection: React.FC<{
  children: React.ReactNode
  isMobileDevice: boolean
}> = ({ children, isMobileDevice }) => {
  const [isVisible, setIsVisible] = useState(true)
  const [isClient, setIsClient] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsClient(true)

    if (globalThis.window === undefined || !('IntersectionObserver' in globalThis.window)) {
      setIsVisible(true)
      return
    }

    const currentElement = sectionRef.current
    if (!currentElement) return

    setIsVisible(false)

    const effectiveRootMargin = isMobileDevice ? '100px' : '300px'
    const effectiveThreshold = isMobileDevice ? 0.1 : 0.01

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.unobserve(currentElement)
        }
      },
      {
        rootMargin: effectiveRootMargin,
        threshold: effectiveThreshold,
      }
    )

    const timeoutId = setTimeout(() => {
      if (currentElement) {
        observer.observe(currentElement)
      }
    }, 200)

    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [isMobileDevice])

  const shouldShowContent = !isClient || isVisible
  const placeholderMinHeight = isMobileDevice ? '400px' : '500px'
  const sectionMinHeight = shouldShowContent ? 'auto' : placeholderMinHeight

  return (
    <>
      {/* CSS animasyonu için style */}
      <style jsx>{`
        @keyframes financeSpin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .finance-spinner {
          animation: financeSpin 1s linear infinite;
        }
      `}</style>

      <div
        ref={sectionRef}
        data-testid="lazy-finance-section"
        data-visible={shouldShowContent ? 'true' : 'false'}
        style={{
          minHeight: sectionMinHeight,
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {shouldShowContent ? (
          children
        ) : (
          // Placeholder görünümü (sadece client-side lazy loading için)
          <div
            style={{
              width: '100%',
              height: isMobileDevice ? '400px' : '500px',
              backgroundColor: '#f0f8ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              border: '1px solid #e6f3ff',
              margin: '20px 0',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                className="finance-spinner"
                style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid #e6f3ff',
                  borderTop: '3px solid #0066cc',
                  borderRadius: '50%',
                }}
              />
              <div
                style={{
                  color: '#0066cc',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                ...
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const Finance = () => {
  const isMobile = useIsMobile()
  const { isTablet } = useDeviceType()
  const { t } = useTranslation('common')
  const { domain, market } = useAppState()
  const domainName = domain === 'tr' ? 'enuygun' : 'wingie'
  const isMobileDevice = !!(isMobile || isTablet)

  const financeContainerRef = useRef<HTMLDivElement>(null)

  // Finance card click tracking
  const handleFinanceCardClick = useCallback(
    (text: string, href: string, order: number) => {
      const environment = isMobileDevice ? 'MOBILE_WEB' : 'DESKTOP_WEB'
      const requestId = getAnalyticsRequestId()

      sendTrackerLog({
        category: domainName === 'enuygun' ? 'enuygun-homepage' : 'wingie-homepage',
        page: domainName === 'enuygun' ? 'enuygun-homepage' : 'wingie-homepage',
        type: 'click',
        message: 'finance_card_click',
        environment: environment,
        requestId,
        cardText: text,
        linkUrl: href,
        cardOrder: order,
        isMobile: !!isMobileDevice,
        market: market,
      })
    },
    [isMobileDevice, market, domainName]
  )

  // Finance logo click tracking
  const handleFinanceLogoClick = useCallback(() => {
    const environment = isMobileDevice ? 'MOBILE_WEB' : 'DESKTOP_WEB'
    const requestId = getAnalyticsRequestId()

    sendTrackerLog({
      category: domainName === 'enuygun' ? 'enuygun-homepage' : 'wingie-homepage',
      page: domainName === 'enuygun' ? 'enuygun-homepage' : 'wingie-homepage',
      type: 'click',
      message: 'finance_logo_click',
      environment: environment,
      requestId,
      linkUrl: 'https://www.enuygunfinans.com/',
      isMobile: !!isMobileDevice,
      market: market,
    })
  }, [isMobileDevice, market, domainName])

  // App Store link click tracking
  const handleAppStoreClick = useCallback(() => {
    const environment = isMobileDevice ? 'MOBILE_WEB' : 'DESKTOP_WEB'
    const requestId = getAnalyticsRequestId()

    sendTrackerLog({
      category: domainName === 'enuygun' ? 'enuygun-homepage' : 'wingie-homepage',
      page: domainName === 'enuygun' ? 'enuygun-homepage' : 'wingie-homepage',
      type: 'click',
      message: 'finance_app_store_click',
      environment: environment,
      requestId,
      linkUrl: 'https://enuygunfinans.go.link?adj_t=1uxfv9xo',
      isMobile: !!isMobileDevice,
      market: market,
    })
  }, [isMobileDevice, market, domainName])

  // Google Play Store link click tracking
  const handleGooglePlayClick = useCallback(() => {
    const environment = isMobileDevice ? 'MOBILE_WEB' : 'DESKTOP_WEB'
    const requestId = getAnalyticsRequestId()

    sendTrackerLog({
      category: domainName === 'enuygun' ? 'enuygun-homepage' : 'wingie-homepage',
      page: domainName === 'enuygun' ? 'enuygun-homepage' : 'wingie-homepage',
      type: 'click',
      message: 'finance_google_play_click',
      environment: environment,
      requestId,
      linkUrl: 'https://enuygunfinans.go.link?adj_t=1uxfv9xo',
      isMobile: !!isMobileDevice,
      market: market,
    })
  }, [isMobileDevice, market, domainName])

  // Mobile download button click tracking
  const handleMobileDownloadClick = useCallback(() => {
    const environment = isMobileDevice ? 'MOBILE_WEB' : 'DESKTOP_WEB'
    const requestId = getAnalyticsRequestId()

    sendTrackerLog({
      category: domainName === 'enuygun' ? 'enuygun-homepage' : 'wingie-homepage',
      page: domainName === 'enuygun' ? 'enuygun-homepage' : 'wingie-homepage',
      type: 'click',
      message: 'finance_mobile_download_click',
      environment: environment,
      requestId,
      linkUrl: 'https://enuygunfinans.go.link/5bK0o',
      isMobile: !!isMobileDevice,
      market: market,
    })
  }, [isMobileDevice, market, domainName])

  return (
    <div ref={financeContainerRef}>
      <LazyFinanceSection isMobileDevice={isMobileDevice}>
        {isMobileDevice ? (
          <div>
            <S.FinanceContainer isTablet={isTablet}>
              <NextImage
                alt="Enuygun finans arkaplan"
                src={'https://cdn2.enuygun.com/media/lib/uploads/image/finance-mobil-bg-48202.png'}
                loading="lazy"
                fill
                data-testid={`${domainName}-homepage-finance-mobile-image`}
              />
              <div style={{ position: 'relative', zIndex: 2, marginTop: 24 }}>
                <a
                  href="https://www.enuygunfinans.com/"
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`${domainName}-homepage-finance-mobile-logo-link`}
                  onClick={handleFinanceLogoClick}
                >
                  <Image
                    unoptimized
                    src={'https://cdn2.enuygun.com/media/lib/uploads/image/enuygun-finans-48191.png'}
                    width={300}
                    height={28}
                    alt="enuygun-finans"
                    loading="lazy"
                    data-testid={`${domainName}-homepage-finance-mobile-logo-image`}
                  />
                </a>
                <div>
                  <S.FinanceHeadingText dangerouslySetInnerHTML={{ __html: t('finance.title') }} data-testid={`${domainName}-homepage-finance-mobile-heading-text`}>
                    {/* Finans işlemlerinizde güvenilir adres:
                <br /> Enuygun Finans */}
                  </S.FinanceHeadingText>
                  <S.FinanceHeadingSubText dangerouslySetInnerHTML={{ __html: t('finance.subTitle') }} data-testid={`${domainName}-homepage-finance-mobile-subHeading-text`}>
                    {/* İhtiyaç kredisi başvurusundan kredi kartına, mevduat hesaplamadan döviz ve
                <br /> altın kurlarına finansla ilgili her şeyi Enuygun Finans’ta bulabilirsiniz. */}
                  </S.FinanceHeadingSubText>
                </div>
              </div>
              <SnapCarousel
                options={{
                  perPage: isTablet ? 5.25 : 2.67,
                  gap: 16,
                }}
                dataTestId="finance-carousel"
                containerWidth={1120}
                isMobile={isMobileDevice}
              >
                {getFinanceCardData(isTablet).map(({ icon, text, href }, key) => (
                  <S.FinanceCard
                    key={href}
                    title={text}
                    href={href}
                    target="_blank"
                    data-testid={`${domainName}-homepage-finance-item-${key}-card`}
                    onClick={() => handleFinanceCardClick(text, href, key + 1)}
                  >
                    <S.FinanceIcon isMobile={isMobileDevice}>{icon}</S.FinanceIcon>
                    <S.FinanceCardTextWrapper>
                      <S.FinanceCardText data-testid={`${domainName}-homepage-finance-item-${key}-text`}>{text}</S.FinanceCardText>
                    </S.FinanceCardTextWrapper>
                  </S.FinanceCard>
                ))}
              </SnapCarousel>
              <S.DownloadAppWrapper isMobile={isMobileDevice}>
                <S.IconOptions>
                  <EnFinans width={55} height={55} />
                </S.IconOptions>
                <S.DownloadAppText
                  dangerouslySetInnerHTML={{ __html: t('finance.mobileDownloadText') }}
                  data-testid={`${domainName}-homepage-finance-mobile-download-text`}
                ></S.DownloadAppText>
                <S.DownloadBtn href="https://enuygunfinans.go.link/5bK0o" data-testid={`${domainName}-homepage-finance-mobile-download-btn`} onClick={handleMobileDownloadClick}>
                  <S.DownloadText data-testid={`${domainName}-homepage-finance-mobile-download-btn-text`}>İndir</S.DownloadText>
                </S.DownloadBtn>
              </S.DownloadAppWrapper>
            </S.FinanceContainer>
          </div>
        ) : (
          <S.FinanceContainer>
            <NextImage alt="Enuygun finans arkaplan" src={'https://cdn2.enuygun.com/media/lib/uploads/image/finance-comp-bg-48190.png'} loading="lazy" fill />
            <Content>
              <S.FinanceHeading>
                <div>
                  <S.FinanceLogo href="https://www.enuygunfinans.com/" target="_blank" rel="noreferrer" title="Enuygun Finans" onClick={handleFinanceLogoClick}>
                    <Image
                      unoptimized
                      src={'https://cdn2.enuygun.com/media/lib/uploads/image/enuygun-finans-48191.png'}
                      width={300}
                      height={30}
                      alt="enuygun-finans"
                      loading="lazy"
                    />
                  </S.FinanceLogo>
                </div>
                <div>
                  <S.FinanceHeadingText data-testid={`${domainName}-homepage-finance-title`}>{t('finance.title')}</S.FinanceHeadingText>
                  <S.FinanceHeadingSubAreaWrapper>
                    <S.LeftWrapper>
                      <S.FinanceHeadingSubText data-testid={`${domainName}-homepage-finance-subTitle`}>{t('finance.subTitle')}</S.FinanceHeadingSubText>
                      <S.SecondaryHeadingText data-testid={`${domainName}-homepage-finance-secondaryTitle`}>{t('finance.secondaryTitle')}</S.SecondaryHeadingText>
                    </S.LeftWrapper>
                    <S.StoreAndQrWrapper>
                      <S.StoreWrapper data-testid={`${domainName}-homepage-finance-storeWrapper`}>
                        <button type="button" style={storeButtonResetStyle} onClick={handleAppStoreClick}>
                          <AppStoreLink url="https://enuygunfinans.go.link?adj_t=1uxfv9xo" height="28px" width="25px" />
                        </button>
                        <button type="button" style={storeButtonResetStyle} onClick={handleGooglePlayClick}>
                          <GooglePlayStoreLink url="https://enuygunfinans.go.link?adj_t=1uxfv9xo" height="28px" width="25px" />
                        </button>
                      </S.StoreWrapper>
                      <S.QrWrapper>
                        <QRCode value="https://enuygunfinans.go.link/?adj_t=1j2kjaya" size={90} data-testid={`${domainName}-homepage-finance-qrCode`} />
                      </S.QrWrapper>
                    </S.StoreAndQrWrapper>
                  </S.FinanceHeadingSubAreaWrapper>
                </div>
              </S.FinanceHeading>
              <S.FinanceCards>
                {getFinanceCardData(isTablet).map(({ icon, text, href }, key) => {
                  return (
                    <S.FinanceCard
                      link={text}
                      key={href}
                      href={href}
                      title={text}
                      target="_blank"
                      data-testid={`${domainName}-homepage-finance-item-${key}-card`}
                      onClick={() => handleFinanceCardClick(text, href, key + 1)}
                    >
                      <S.FinanceIcon isMobile={isMobileDevice}>{icon}</S.FinanceIcon>
                      <S.FinanceCardText data-testid={`${domainName}-homepage-finance-item-${key}-text`}>{text}</S.FinanceCardText>
                    </S.FinanceCard>
                  )
                })}
              </S.FinanceCards>
            </Content>
          </S.FinanceContainer>
        )}
      </LazyFinanceSection>
    </div>
  )
}

export default Finance
